import React, { useState } from "react";
import {
  Table,
  Grid,
  Header,
  Button,
  Divider,
  Segment,
  Message,
  Loader,
} from "semantic-ui-react";
import { HierarchicalSupplyUnitsList } from "./HierchichalSupplyUnitDisplay";
import axios from "../../../apis/dvdlocator";
import isEqual from "lodash/isEqual";
import type { WizardSupplyUnit, ProductData } from "./AddSupplyUnitWizard/types";

interface ReviewSelectionPageProps {
  product: ProductData;
  initialProduct?: ProductData | null;
  setProduct: (product: ProductData) => void;
  onClose: (didCreate?: boolean) => void;
  prevPage: () => void;
  supplyUnits: WizardSupplyUnit[];
  mode?: "create" | "duplicate" | "edit";
}

export function ReviewSelectionPage({
  product,
  initialProduct,
  setProduct,
  onClose,
  prevPage,
  supplyUnits,
  mode,
}: ReviewSelectionPageProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const createProduct = async (): Promise<number> => {
    const res = await axios.post("product", {
      product,
    });
    setProduct({ ...product, product_id: res.data.id });
    return res.data.id;
  };

  const editProduct = async (): Promise<number> => {
    const res = await axios.put(`product/${product.product_id}`, { product });
    setProduct({ ...product, product_id: res.data.id });
    return res.data.id;
  };

  const linkProductInventory = async (productId: number, clearExisting?: boolean) => {
    const uniqueInventoryRecords = Array.from(
      supplyUnits.reduce((map, unit) => {
        map.set(unit.inventory_id, {
          inventoryId: unit.inventory_id,
          partNumber: unit.part_number,
        });
        return map;
      }, new Map<number, { inventoryId: number; partNumber: string }>()),
      ([, value]) => value
    );

    if (uniqueInventoryRecords.length === 0) return;

    await axios.post(`product/${productId}/inventory`, {
      inventoryRecords: uniqueInventoryRecords,
      clearExisting,
    });
  };

  const handleCreateSubmission = async () => {
    const productId = await createProduct();
    await linkProductInventory(productId);
    setSuccess(true);
  };

  const handleEditSubmission = async () => {
    const productDataChanged = !isEqual(initialProduct, product);
    if (productDataChanged) await editProduct();
    await linkProductInventory(product.product_id!, true);
    setSuccess(true);
  };

  const submitForm = async () => {
    setIsLoading(true);
    setError(null);
    try {
      if (mode === "edit") {
        await handleEditSubmission();
      } else {
        await handleCreateSubmission();
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <Segment basic>
        <Message positive>
          <Message.Header>
            Product Successfully {mode === "edit" ? "Updated" : "Created"}!
          </Message.Header>
        </Message>
        <Table celled>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Title</Table.HeaderCell>
              <Table.HeaderCell>Part Number</Table.HeaderCell>
              <Table.HeaderCell>Families</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            <Table.Row>
              <Table.Cell>{product.title}</Table.Cell>
              <Table.Cell>{product.partNumber}</Table.Cell>
              <Table.Cell>
                {product.families.map((fam) => fam.title).join(", ")}
              </Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table>
        <Divider hidden />
        <Segment>
          <Button positive onClick={() => onClose(true)}>
            Close
          </Button>
        </Segment>
      </Segment>
    );
  }

  return (
    <>
      <Segment basic>
        {error && (
          <Message negative>
            <Message.Header>Error</Message.Header>
            <p>{error}</p>
          </Message>
        )}
        <p style={{ color: "gray" }}>
          Review your selections and click submit to {mode === "edit" ? "update" : "create"} this
          product.
        </p>
        <Table celled>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Title</Table.HeaderCell>
              <Table.HeaderCell>Part Number</Table.HeaderCell>
              <Table.HeaderCell>Families</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            <Table.Row>
              <Table.Cell>{product.title}</Table.Cell>
              <Table.Cell>{product.partNumber}</Table.Cell>
              <Table.Cell>
                {product.families.map((fam) => fam.title).join(", ")}
              </Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table>
        <Divider section />
        <Grid>
          <Grid.Row>
            <Grid.Column floated="left" width={8}>
              <Header as="h3">Linked Supply Units</Header>
            </Grid.Column>
            <Grid.Column width={8} />
          </Grid.Row>
        </Grid>
        {supplyUnits.length > 0 && (
          <HierarchicalSupplyUnitsList supplyUnits={supplyUnits} interactable={false} />
        )}
      </Segment>
      <Divider />
      <Segment basic>
        <Grid>
          <Grid.Row>
            <Grid.Column width={8}>
              <Button onClick={prevPage} floated="left" disabled={isLoading}>
                Back
              </Button>
            </Grid.Column>
            <Grid.Column width={8}>
              <Button primary onClick={submitForm} floated="right" disabled={isLoading}>
                {isLoading ? <Loader active inline size="tiny" /> : "Submit"}
              </Button>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Segment>
    </>
  );
}
